import DynamicDialog from '@/components/DynamicDialog';
import useFormSchema from '@/hooks/useFormSchema';
import { useFetchOrganisations } from '@/http/queries';
import Grid from '@mui/material/Grid2';
import { IconUsersGroup } from '@tabler/icons-react';
import { TextField } from 'mui-rff';
import { Form } from 'react-final-form';
import * as yup from 'yup';
import { noLeadingOrTrailingSpaces } from '@/helpers/yupValidation';
import { useMemo } from 'react';
import validateDomain from '@/pages/admin/validateDomain';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation.tsx';

export default function OrgDialog({
  orgData,
  usePost,
  onSuccess,
  onFail,
  initialValues,
  ...dialogProps
}) {
  const { t, ts } = useInlineTranslation();

  const { data: orgList } = useFetchOrganisations('all', !!orgData);

  const { mutateAsync: postOrgAsync } = usePost();

  const { orgNames, orgDomains } = useMemo(() => {
    return (orgList ?? []).reduce(
      (acc, org) => {
        acc.orgNames.push(org.name);
        acc.orgDomains.push(org.domain);
        return acc;
      },
      { orgNames: [], orgDomains: [] },
    );
  }, [orgList]);

  const { validate, required } = useFormSchema(
    yup.object({
      name: noLeadingOrTrailingSpaces(t('organisationName'))
        .notOneOf(orgNames, t('organisationExist'))
        .max(200)
        .required(),
      domain: noLeadingOrTrailingSpaces(t('domain'))
        .test('valid-domain', function (value) {
          const raw = (value ?? '').trim();
          if (!raw) return true; // .required() behandelt "leer"

          const domain = raw.split(/[\s,]+/).filter(Boolean)[0] ?? '';
          const { isError, textError } = validateDomain(domain);

          return isError ? this.createError({ message: textError }) : true;
        })
        .notOneOf(orgDomains, t('domainExist'))
        .required(),
    }),
  );

  async function onSubmit(values, form) {
    try {
      const response = await postOrgAsync({
        name: values.name.trim(),
        domain: values.domain.trim(),
      });

      if (onSuccess) {
        onSuccess(response);
      }

      form.restart();
      return true;
    } catch (error) {
      if (onFail) {
        onFail(error);
      }
      return false;
    }
  }

  return (
    <Form
      validate={validate}
      onSubmit={onSubmit}
      initialValues={initialValues}
      render={({ handleSubmit }) => (
        <form noValidate onSubmit={handleSubmit}>
          <DynamicDialog
            {...dialogProps}
            title={t('addOrganisation')}
            confirmBtnText={t('save')}
            activatorText={t('addOrganisation')}
            activatorProps={{
              color: 'primary',
              variant: 'contained',
              startIcon: <IconUsersGroup />,
              ...dialogProps?.activatorProps,
            }}
            onConfirm={handleSubmit}
          >
            <Grid
              container
              sx={{
                alignItems: 'center',
                gap: 2.5,
                justifyContent: 'center',
                my: 2.5,
                px: { xs: 3, md: 4, xl: 5.5 },
              }}
            >
              <TextField
                name='name'
                label={t('organisationName')}
                required={required.name}
                autoFocus
                autoComplete='new-password'
                fullWidth
              />
              <TextField
                name='domain'
                label={t('domain')}
                placeholder={ts('selectDomain')}
                helperText={t('domainHelperText')}
                required={required.domain}
                autoComplete='off'
                fullWidth
              />
            </Grid>
          </DynamicDialog>
        </form>
      )}
    />
  );
}
